import * as THREE from 'three';

// ============================================================
// 3D player marker — floating diamond + username label
// ============================================================

const MARKER_SIZE = 1.5;
const MARKER_HEIGHT = 15;
const LABEL_HEIGHT = 10; // between vehicle and diamond
const BOB_SPEED = 2;
const BOB_AMOUNT = 1;

const diamondGeom = new THREE.OctahedronGeometry(MARKER_SIZE, 0);

function createLabelSprite(username: string, color: number): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;

  ctx.clearRect(0, 0, 256, 64);
  ctx.font = 'bold 32px Consolas, Courier New, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Convert hex color to CSS string
  const hex = `#${color.toString(16).padStart(6, '0')}`;

  // Drop shadow
  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.fillText(username, 130, 34);

  // Text
  ctx.fillStyle = hex;
  ctx.fillText(username, 128, 32);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;

  const mat = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
  });

  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(12, 3, 1);
  sprite.renderOrder = 998;
  return sprite;
}

export class EnemyMarker {
  readonly marker: THREE.Mesh;
  private readonly label: THREE.Sprite;
  private readonly target: THREE.Object3D;
  private readonly material: THREE.MeshBasicMaterial;
  private time = 0;

  constructor(target: THREE.Object3D, scene: THREE.Scene, color: number, username: string) {
    this.target = target;
    this.material = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.85,
      depthTest: false,
    });
    this.material.color.setHex(color);
    this.marker = new THREE.Mesh(diamondGeom, this.material);
    this.marker.renderOrder = 999;
    this.marker.frustumCulled = false;
    scene.add(this.marker);

    // Username label between vehicle and diamond
    this.label = createLabelSprite(username, color);
    this.label.frustumCulled = false;
    scene.add(this.label);
  }

  update(dt: number, camera: THREE.Camera): void {
    this.time += dt;

    // Hide if target is hidden
    const visible = this.target.visible;
    this.marker.visible = visible;
    this.label.visible = visible;
    if (!visible) return;

    // Diamond: above target with bobbing
    const bob = Math.sin(this.time * BOB_SPEED) * BOB_AMOUNT;
    this.marker.position.copy(this.target.position);
    this.marker.position.y += MARKER_HEIGHT + bob;

    // Label: between vehicle and diamond
    this.label.position.copy(this.target.position);
    this.label.position.y += LABEL_HEIGHT;

    // Rotate diamond slowly
    this.marker.rotation.y += dt * 1.5;

    // Scale both based on distance
    const dist = camera.position.distanceTo(this.marker.position);
    const scale = THREE.MathUtils.clamp(dist / 300, 0.8, 4);
    this.marker.scale.setScalar(scale);
    this.label.scale.set(12 * scale, 3 * scale, 1);
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.marker);
    scene.remove(this.label);
    this.material.dispose();
    (this.label.material as THREE.SpriteMaterial).map?.dispose();
    (this.label.material as THREE.SpriteMaterial).dispose();
  }
}
